/**
 * courier.mjs — a round-trip, as the five steps and the two clocks.
 *
 * FOUNDER, 2026-08-14: "ch2 next".
 *
 * ── WHAT CHAPTER 2 TURNS ON ───────────────────────────────────────────────────────────────────
 * Its own sentence, and the lab is built to make it operable rather than readable:
 *
 *   "This single distinction — is the bottleneck the machine or the network? — is the kind of
 *    question a literate user starts asking instead of just feeling frustrated."
 *
 * So this engine computes a round trip in two parts and keeps them apart: the JOURNEY, which is the
 * courier, and the WORK, which is the machine. A readout that only showed a total would teach the
 * opposite — that "slow" is one thing — which is exactly the confusion the section is written to
 * clear. Every number below is quoted from the module; none is a plausible figure I chose.
 */

/**
 * §4, verbatim: "A request to a machine in the same city might take 20 milliseconds. The same
 * request to a machine on a different continent might take 250 milliseconds. The same request from
 * a village with poor connectivity might take 3 seconds or fail entirely."
 *
 * These are ROUND-TRIP figures — the section defines latency as "the time the whole round-trip
 * takes" — so they are never doubled. Getting that wrong would put 500 ms on a continental hop and
 * quietly teach a number the module does not give.
 */
export const PLACES = Object.freeze([
  { id: 'city', label: 'a machine in the same city', latencyMs: 20 },
  { id: 'continent', label: 'a machine on a different continent', latencyMs: 250 },
  { id: 'village', label: 'a village with poor connectivity', latencyMs: 3000, mayFail: true },
]);
export const PLACES_SOURCE = 'Code101 module 02 §4 "The journey — packets, hops, time"';

/** §5, verbatim, in his order and his words. The lab may not rename or reorder these. */
export const STEPS = Object.freeze([
  { n: 1, name: 'Translation', who: 'network', text: 'Your browser asks DNS: "what is the IP address for gmail.com?" DNS answers within milliseconds.' },
  { n: 2, name: 'Request', who: 'you', text: 'Your browser builds a request: "to the machine at that IP, please send me the Gmail homepage."' },
  { n: 3, name: 'Journey', who: 'network', text: 'The packets hop through your home router, your internet provider\'s routers, undersea cables, more routers, until they reach Google\'s machines.' },
  { n: 4, name: 'Work', who: 'machine', text: 'A Google machine reads the request, runs the Gmail homepage recipe, and produces the answer: the page contents.' },
  { n: 5, name: 'Reply', who: 'network', text: 'The answer is broken into packets, hopped back to your machine, reassembled, and your browser starts rendering.' },
]);
export const STEPS_SOURCE = 'Code101 module 02 §5 "What happens when you open a website — the whole flow"';

/** §6's three failures, in his words. The verdict line is his too: "is it me, is it the courier, or is it them?" */
export const FAULTS = Object.freeze({
  none: { id: 'none', label: 'nothing wrong', blame: null },
  noConnection: {
    /* FAILS AT STEP 1, not step 2. Translation is the DNS lookup and DNS travels over the same
       network — with no signal there is nobody to ask. The first cut failed at Request and the lab
       showed DNS answering happily on a phone with no connection, which is a small picture telling
       a confident lie. §6: "Every request fails immediately." */
    id: 'noConnection', label: 'No connection', failsAt: 1, blame: 'me',
    text: 'Your machine cannot reach the courier at all. No Wi-Fi, no mobile data, no signal. Every request fails immediately.',
    verdict: 'This is the cleanest failure — you know exactly what is wrong.',
  },
  slow: {
    id: 'slow', label: 'Slow connection', failsAt: null, blame: 'the courier', slowdown: 8,
    text: 'Requests travel, but very slowly. Apps feel sluggish. Some features may give up after a timeout (waiting too long for an answer, the app stops waiting and treats it as failed).',
    verdict: 'This is a more confusing failure because the app is partly working.',
  },
  serverDown: {
    id: 'serverDown', label: 'The remote machine is down or refusing', failsAt: 4, blame: 'them',
    text: 'Your end is fine, the courier is fine, but the destination machine is not answering — it has crashed, or is overloaded, or has rejected your specific request (e.g., "you are not allowed to see this").',
    verdict: 'Apps show errors like "500 Internal Server Error" or "Service Unavailable."',
  },
});
export const FAULTS_SOURCE = 'Code101 module 02 §6 "What it costs and how it fails"';
export const THE_QUESTION = 'A literate user, when something does not work, asks: is it me, is it the courier, or is it them?';

/**
 * One round trip. Returns the five steps with a duration each, and — the point of the whole thing —
 * the total split into JOURNEY and WORK.
 *
 * The network total is the place's latency SPREAD across the three network steps rather than added
 * to each: latency is defined as the whole round trip, so charging it three times would inflate a
 * continental hop from 250 ms to 750 ms and teach a figure the module does not give.
 */
export function roundTrip({ place = 'continent', workMs = 60, fault = 'none' } = {}) {
  const p = PLACES.find((x) => x.id === place) || PLACES[1];
  const f = FAULTS[fault] || FAULTS.none;
  const netTotal = p.latencyMs * (f.slowdown || 1);
  const netSteps = STEPS.filter((s) => s.who === 'network').length;

  let stopped = null;
  let netSeen = 0;
  const steps = STEPS.map((s) => {
    if (stopped) return { ...s, ms: 0, reached: false };
    if (f.failsAt === s.n) { stopped = s.n; return { ...s, ms: 0, reached: false, failedHere: true }; }
    /* The LAST network step absorbs the remainder so the three add to the module's figure exactly.
       Dividing 250 by three and rounding gave 83+83+83 = 249, and a lab that prints 249 ms beside a
       module that says 250 ms is quietly disagreeing with its own source. */
    netSeen += s.who === 'network' ? 1 : 0;
    const even = Math.round(netTotal / netSteps);
    const ms = s.who === 'network'
      ? (netSeen === netSteps ? netTotal - even * (netSteps - 1) : even)
      : (s.who === 'machine' ? workMs : 0);
    return { ...s, ms, reached: true };
  });

  const done = steps.filter((s) => s.reached);
  const journeyMs = done.filter((s) => s.who === 'network').reduce((t, s) => t + s.ms, 0);
  const machineMs = done.filter((s) => s.who === 'machine').reduce((t, s) => t + s.ms, 0);
  const totalMs = journeyMs + machineMs;

  return {
    steps, journeyMs, machineMs, totalMs,
    arrived: !stopped,
    failedAt: stopped,
    blame: stopped ? f.blame : null,
    /* THE ANSWER TO HIS QUESTION, computed and only when it can be. With nothing wrong there is
       still a bottleneck and it is worth naming; with a failure the blame is the fault's own. */
    bottleneck: stopped ? null : (journeyMs === machineMs ? 'even' : (journeyMs > machineMs ? 'the network' : 'the machine')),
    /* The share of the wait that was NOT the machine. §5: "Most of that time was not 'Google
       thinking' — it was the journey." */
    journeyShare: totalMs ? Math.round((100 * journeyMs) / totalMs) : 0,
  };
}

/** §5: "That entire round-trip happened in 200 to 800 milliseconds." */
export const GMAIL_RANGE = Object.freeze({ lowMs: 200, highMs: 800, source: STEPS_SOURCE });
export const inGmailRange = (ms) => ms >= GMAIL_RANGE.lowMs && ms <= GMAIL_RANGE.highMs;

/** §7's translations, quoted. A marketing word, and what it means in requests and answers. */
export const TRANSLATIONS = Object.freeze([
  { phrase: 'Real-time', means: 'fast enough round-trips that the delay does not show. Not actually instant. Just fast.' },
  { phrase: 'Always connected', means: 'the app makes requests so frequently (or holds a connection open) that it always has fresh answers ready.' },
  { phrase: 'Synced across devices', means: 'every device is making requests to the same far-away machine, so they all see the same answer.' },
  { phrase: 'Works offline', means: 'the app keeps a copy of the most recent answers and lets you read them without making new requests.' },
  { phrase: 'Cloud-based', means: 'the actual work is happening on far-away machines, not on yours.' },
  { phrase: 'Live', means: 'your machine has a request running right now, waiting for the answer that is in the middle of being prepared.' },
]);
export const TRANSLATIONS_SOURCE = 'Code101 module 02 §7 "The marketing translation — what “online” words actually mean"';
